import React, { useId } from 'react';
import { clsx } from 'clsx';

export interface SwitchProps {
  /** Label text displayed next to the switch */
  label?: string;
  /** Description text below the label */
  description?: string;
  /** Controlled checked state */
  checked?: boolean;
  /** Change handler */
  onChange?: (checked: boolean) => void;
  /** Switch size */
  size?: 'sm' | 'md';
  /** Disabled state */
  disabled?: boolean;
  /** Additional CSS classes */
  className?: string;
  /** Element id */
  id?: string;
  /**
   * Nome acessível explícito, para quando não há `label` visível (o texto já
   * existe em outro lugar da tela, fora do Switch, e não deve ser duplicado).
   */
  'aria-label'?: string;
  /**
   * Aponta para o(s) `id`(s) de elemento(s) de texto já existentes que nomeiam
   * o controle (pode listar mais de um id, separado por espaço — o nome
   * acessível vira a concatenação do texto de cada um, na ordem). Tem
   * prioridade sobre o `aria-labelledby` interno gerado quando `label` é
   * passado.
   */
  'aria-labelledby'?: string;
  'data-testid'?: string;
}

/**
 * Shared Switch/Toggle component with smooth animations.
 *
 * @example
 * ```tsx
 * <Switch
 *   label="Ativar notificações"
 *   description="Receba alertas em tempo real"
 *   checked={notifs}
 *   onChange={setNotifs}
 * />
 * ```
 */
export const Switch: React.FC<SwitchProps> = ({
  label,
  description,
  checked = false,
  onChange,
  size = 'md',
  disabled = false,
  className,
  id: propId,
  'aria-label': ariaLabel,
  'aria-labelledby': ariaLabelledBy,
  'data-testid': dataTestId,
}) => {
  const generatedId = useId();
  const inputId = propId || generatedId;

  // `.tgl` do mockup: 38×22 knob 18 (translate 16) · `.tgl.sm` 32×18 knob 14 (translate 14)
  const sizes = {
    sm: { track: { width: 32, height: 18 }, thumb: 14, translate: 14 },
    md: { track: { width: 38, height: 22 }, thumb: 18, translate: 16 },
  };

  const s = sizes[size];

  return (
    <div className={clsx('flex items-center gap-3 min-h-[38px]', className)}>
      <button
        type="button"
        role="switch"
        aria-checked={checked}
        aria-label={ariaLabel}
        aria-labelledby={ariaLabelledBy ?? (label ? `${inputId}-label` : undefined)}
        id={inputId}
        disabled={disabled}
        onClick={() => onChange?.(!checked)}
        data-testid={dataTestId}
        className="relative inline-flex flex-shrink-0 rounded-full cursor-pointer disabled:cursor-not-allowed disabled:opacity-50"
        style={{
          width: `${s.track.width}px`,
          height: `${s.track.height}px`,
          backgroundColor: checked ? 'var(--theme-primary)' : 'var(--theme-input-border)',
          transition: 'background-color 150ms',
          padding: '2px',
        }}
      >
        <span
          className="block rounded-full shadow-sm"
          style={{
            width: `${s.thumb}px`,
            height: `${s.thumb}px`,
            backgroundColor: '#ffffff',
            transform: `translateX(${checked ? s.translate : 0}px)`,
            transition: 'transform 150ms',
          }}
        />
      </button>

      {(label || description) && (
        <div className="flex-1 min-w-0">
          {label && (
            <label
              id={`${inputId}-label`}
              htmlFor={inputId}
              className="text-sm font-medium cursor-pointer select-none"
              style={{ color: 'var(--theme-text)' }}
              onClick={() => !disabled && onChange?.(!checked)}
            >
              {label}
            </label>
          )}
          {description && (
            <p className="text-xs mt-0.5" style={{ color: 'var(--theme-muted-text)' }}>
              {description}
            </p>
          )}
        </div>
      )}
    </div>
  );
};

Switch.displayName = 'Switch';
